import type { FormulaContext } from './formula'

// '=' 수식 자동완성 — 지금 커서 앞 글자를 보고 넣을 수 있는 이름을 고른다.
//  · '=' 직후나 연산자(+ - * / & ( ) 뒤: 쓸 수 있는 것 전부 ({ROW} {설비구분} ↑+1 …)
//  · '{inter' 처럼 괄호를 연 채 치는 중: 그 글자로 거른 이름 — 고르면 닫는 괄호까지 넣는다

export interface Suggestion {
  label: string // 목록에 보이는 글자
  insert: string // 넣을 글자
  hint: string
  preview?: string // 지금 디바이스의 실제 값
}

export interface SuggestState {
  items: Suggestion[]
  from: number // 바꿀 범위 (value 안 위치)
  to: number
  title?: string // 목록 머리 안내 (예: "cnc_rdaxisdata — B: type (2/4)")
}

type Device = FormulaContext['device']

const NAMES: { name: string; hint: string; value?: (d: Device) => string }[] = [
  { name: 'ROW', hint: '행 번호' },
  { name: '설비구분', hint: '디바이스', value: d => d.category },
  { name: '제조사', hint: '디바이스', value: d => d.maker },
  { name: '모델', hint: '디바이스', value: d => d.model },
  { name: '디바이스', hint: '디바이스 이름', value: d => d.name },
  { name: 'code', hint: '디바이스 코드', value: d => d.code },
  { name: '주소', hint: '같은 행' },
  { name: '크기', hint: '같은 행' },
  { name: '설명', hint: '같은 행' },
  { name: 'Alias', hint: '같은 행' },
]

export function suggest(value: string, caret: number, device: Device): SuggestState | null {
  if (!value.trimStart().startsWith('=')) return null
  const before = value.slice(0, caret)

  // 괄호를 연 채 치는 중 — {inter / [설
  const open = /([{[])([^{}[\]]*)$/.exec(before)
  if (open) {
    const [opener, partial] = [open[1], open[2].replace(/[\s_-]+/g, '').toLowerCase()]
    const closer = opener === '{' ? '}' : ']'
    const items = NAMES
      .filter(n => n.name.toLowerCase().includes(partial))
      .map(n => toItem(n, `${opener}${n.name}${closer}`, device))
    const from = before.length - open[0].length
    const to = value[caret] === closer ? caret + 1 : caret
    return items.length ? { items, from, to } : null
  }

  // '=' 직후·연산자 뒤 — 전부
  if (/^\s*=\s*$|[+\-*/&(]\s*$/.test(before)) {
    const items = [
      ...NAMES.map(n => toItem(n, `{${n.name}}`, device)),
      { label: '↑+1', insert: '↑+1', hint: '위 칸 + 1 (1-3x4 → 1-3x5)' },
    ]
    return { items, from: caret, to: caret }
  }
  return null
}

function toItem(n: (typeof NAMES)[number], insert: string, device: Device): Suggestion {
  return { label: insert, insert, hint: n.hint, preview: n.value?.(device) || undefined }
}
